<template>
  <Head :title="__('about-us.meta.title')">
    <meta
      name="description"
      :content="__('about-us.meta.description')"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.about-us')"
    >
  </Head>

  <section class="about-hero py-20 relative overflow-hidden">
    <img
      src="/frontend/assets/mobile-hero-swirl.svg"
      class="absolute top-0 left-0 lg:hidden"
    >
    <img
      src="/frontend/assets/desktop-hero-swirl.svg"
      class="absolute top-0 left-0 hidden lg:block"
    >

    <div class="container text-center relative z-20">
      <h1 class="text-5xl font-bold mb-4 lg:text-7xl">
        {{ __('about-us.title') }}
      </h1>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
        <div class="w-full border border-black shadow-vip rounded-lg text-center p-6 bg-white">
          <div class="inline-flex items-center text-6xl text-red font-bold block mb-2">
            <span>
              {{ averageRating }}
            </span>
          </div>
          <span class="text-gray-300 block">{{ __('about-us.rating') }}</span>
        </div>

        <div class="w-full border border-black shadow-vip rounded-lg text-center p-6 bg-white">
          <div class="text-6xl text-red font-bold block mb-2">
            {{ totalPaidShoutOuts }}+
          </div>
          <span class="text-gray-300 block">{{ __('about-us.shout-out-sent') }}</span>
        </div>

        <div class="w-full border border-black shadow-vip rounded-lg text-center p-6 bg-white">
          <div class="text-6xl text-red font-bold block mb-2">
            {{ vipsCount }}+
          </div>
          <span class="text-gray-300 block">{{ __('about-us.connected-vips') }}</span>
        </div>
      </div>
    </div>
  </section>

  <section class="about-text relative py-14 bg-white text-lg">
    <img
      src="/frontend/assets/scribble.svg"
      class="absolute -top-[48px] left-1/2 transform -translate-x-1/2"
    >

    <div class="container">
      <div class="flex flex-wrap">
        <div class="w-full lg:mb-6">
          <h2 class="text-4xl font-bold mb-4 text-center mt-4 lg:px-16 lg:text-5xl">
            {{ __('about-us.intro.title') }}
          </h2>
        </div>

        <div class="w-full md:w-1/2 md:pr-4">
          <p><strong>{{ __('about-us.intro.what-we-do.title') }}</strong></p>
          <p
            v-for="(value, index) in $tm('about-us.intro.what-we-do.text')"
            :key="index"
          >
            {{ value }}
          </p>
        </div>

        <div class="w-full md:w-1/2 md:pl-4">
          <p><strong>{{ __('about-us.intro.history.title') }}</strong></p>
          <p
            v-for="(value, index) in $tm('about-us.intro.history.text')"
            :key="index"
          >
            {{ value }}
          </p>
        </div>

        <div class="w-full">
          <p><strong>{{ __('about-us.intro.summary.title') }}</strong></p>
          <p
            v-for="(value, index) in $tm('about-us.intro.summary.text')"
            :key="index"
          >
            {{ value }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <section class="about-offer relative text-center text-lg">
    <img
      src="/frontend/assets/scribble.svg"
      class="absolute -top-[48px] left-1/2 transform -translate-x-1/2"
    >

    <div class="container py-14 lg:pt-28 relative overflow-hidden">
      <img
        src="/frontend/assets/about.svg"
        class="absolute -right-[80px] top-[10px] lg:right-[200px] lg:top-[50px] md:right-[150px]"
      >

      <h2 class="font-bold text-4xl mt-4 lg:text-5xl">
        {{ __('about-us.what-do-we-offer.title') }}
      </h2>

      <p>
        {{ __('about-us.what-do-we-offer.text') }}
      </p>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-3 md:mt-8">
        <div class="px-6">
          <img
            src="/frontend/assets/offer-1.svg"
            class="mx-auto mb-8"
          >

          <h3 class="text-2xl font-bold mb-4">
            {{ __('about-us.what-do-we-offer.experience.title') }}
          </h3>

          <p>{{ __('about-us.what-do-we-offer.experience.text') }}</p>
        </div>

        <div class="px-6">
          <img
            src="/frontend/assets/offer-2.svg"
            class="mx-auto mb-8"
          >

          <h3 class="text-2xl font-bold mb-4">
            {{ __('about-us.what-do-we-offer.business.title') }}
          </h3>

          <p>{{ __('about-us.what-do-we-offer.business.text') }}</p>
        </div>

        <div class="px-6">
          <img
            src="/frontend/assets/offer-3.svg"
            class="mx-auto mb-8"
          >

          <h3 class="text-2xl font-bold mb-4">
            {{ __('about-us.what-do-we-offer.people.title') }}
          </h3>

          <p>{{ __('about-us.what-do-we-offer.people.text') }}</p>
        </div>
      </div>
    </div>
  </section>

  <Reviews
    title="Populaire VIPS en voorbeelden"
    :show-scribble="true"
    :extended="true"
    class="pt-12 lg:pb-10"
  />

  <section class="about-founders py-10 md:pt-18 md:pb-24">
    <img
      src="/frontend/assets/scribble.svg"
      class="mx-auto mb-4 lg:mb-12 lg:h-[150px]"
    >

    <div class="container">
      <div class="text-center border-b border-gray-200 mb-8 md:mb-12">
        <h2 class="text-5xl font-bold sparkle-top-left inline-block mb-4">
          {{ __('about-us.founders.title') }}
        </h2>

        <p class="text-xl mb-8 md:mb-12">
          {{ __('about-us.founders.text') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:px-8">
        <a
          href="https://www.linkedin.com/in/ferri-somogyi-32b4451/"
          target="_blank"
          class="border-2 border-black rounded-lg p-3 shadow-news bg-white"
        >
          <div class="flex flex-wrap">
            <div class="w-2/5">
              <img
                src="/frontend/assets/ferri.jpeg"
                class="rounded-lg w-full"
              >
            </div>

            <div class="w-3/5 pl-6">
              <span class="text-sm font-medium block">{{ __('about-us.founders.ferri.title') }}</span>
              <h3 class="text-2xl font-bold lg:my-3">
                {{ __('about-us.founders.ferri.name') }}
              </h3>
            </div>
          </div>
        </a>

        <a
          href="https://www.linkedin.com/in/richardklompamsterdam/"
          target="_blank"
          class="border-2 border-black rounded-lg p-3 shadow-news bg-white"
        >
          <div class="flex flex-wrap">
            <div class="w-2/5">
              <img
                src="/frontend/assets/richard.png"
                class="rounded-lg w-full"
              >
            </div>

            <div class="w-3/5 pl-6">
              <span class="text-sm font-medium block">{{ __('about-us.founders.richard.title') }}</span>
              <h3 class="text-2xl font-bold lg:my-3">
                {{ __('about-us.founders.richard.name') }}
              </h3>
            </div>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script>
import Reviews from '../../Components/Reviews.vue';

export default {
  components: {
    Reviews,
  },

  props: {
    averageRating: {
      type: String,
      required: true,
    },

    totalPaidShoutOuts: {
      type: String,
      required: true,
    },

    vipsCount: {
      type: String,
      required: true,
    },
  },
};
</script>
